import { Component, ChangeDetectionStrategy, input, output, inject, OnInit, computed, signal, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntil } from 'rxjs';
import { Destroyable } from '@app/shared/utils/destroyable';
import { CapyStatsService } from '@shared/services/capystats.service';
import { groszeToPln, plnToGrosze } from '@shared/utils/currency.util';
import { ObjectService, ObjectPricingResponse } from '../../services';
import { CreateObjectDto, CreateObjectTypeInlineDto, CreateSeasonInlineDto, ManagedObjectEntity, ObjectTypeEntity, PositioningHint, UpdateImpactResponse } from '../../models';
import { ObjectPositioningWidgetComponent } from '../../components/object-positioning-widget/object-positioning-widget.component';
import { VacationProperty, WizardMode, PropertyBasicInfo, UnitType, PricingRules, PetPolicy, Season, ImageFile, OccupancyPricing, DEFAULT_CONTACT_VISIBILITY } from './models';
import { PropertyWizardStateService, WizardValidationService, SeasonCalculatorService } from './services';
import { WizardHeaderComponent } from './components/wizard-header/wizard-header.component';
import { WizardStepperComponent } from './components/wizard-stepper/wizard-stepper.component';
import { StepBasicInfoComponent } from './components/step-basic-info/step-basic-info.component';
import { StepUnitTypesComponent } from './components/step-unit-types/step-unit-types.component';
import { StepPricingComponent } from './components/step-pricing/step-pricing.component';
import { UpdateImpactDialogComponent } from './components/update-impact-dialog/update-impact-dialog.component';

@Component({
  selector: 'app-property-wizard',
  standalone: true,
  imports: [
    ToastModule,
    ProgressSpinnerModule,
    WizardHeaderComponent,
    WizardStepperComponent,
    StepBasicInfoComponent,
    StepUnitTypesComponent,
    StepPricingComponent,
    UpdateImpactDialogComponent,
    ObjectPositioningWidgetComponent,
  ],
  providers: [
    PropertyWizardStateService,
    WizardValidationService,
    SeasonCalculatorService,
    MessageService
  ],
  templateUrl: './property-wizard.component.html',
  styleUrl: './property-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyWizardComponent extends Destroyable implements OnInit {
  readonly initialData = input<VacationProperty>();
  readonly mode = input<WizardMode>('create');

  readonly formSubmit = output<VacationProperty>();
  readonly formCancel = output<void>();
  readonly stepChange = output<number>();

  private readonly stateService = inject(PropertyWizardStateService);
  private readonly objectService = inject(ObjectService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly capyStats = inject(CapyStatsService);

  readonly submitting = signal(false);
  readonly loadingObject = signal(false);
  readonly loadingStep = signal(false);
  readonly showImpactDialog = signal(false);
  readonly impactLoading = signal(false);
  readonly impactResult = signal<UpdateImpactResponse | null>(null);
  private editObjectId: number | null = null;
  /** Public, reactive mirror of editObjectId for the template (positioning widget). */
  readonly editObjectIdSignal = signal<number | null>(null);
  private pendingDto: CreateObjectDto | null = null;

  /** Track which steps have been loaded (for lazy loading in edit mode) */
  private readonly loadedSteps = new Set<number>();

  readonly currentStep = this.stateService.currentStep;
  readonly steps = this.stateService.steps;
  readonly isViewMode = this.stateService.isViewMode;
  readonly canGoBack = this.stateService.canGoBack;
  readonly canGoNext = this.stateService.canGoNext;
  readonly canSubmit = this.stateService.canSubmit;

  constructor() {
    super();
    // React to step changes from ANY source (child components call stateService directly)
    effect(() => {
      const step = this.currentStep();
      this.capyStats.track('wizard_step_viewed', { stepIndex: step, mode: this.mode() });
      this.ensureStepDataLoaded(step);
    });
  }

  readonly title = computed(() => {
    const mode = this.stateService.mode();
    switch (mode) {
      case 'create':
        return this.translocoService.translate('objects.wizard.title.create');
      case 'edit':
        return this.translocoService.translate('objects.wizard.title.edit');
      case 'view':
        return this.translocoService.translate('objects.wizard.title.view');
      default:
        return this.translocoService.translate('objects.wizard.title.default');
    }
  });

  ngOnInit(): void {
    const idParam = this.route.parent?.snapshot.params['id'];
    const objectId = idParam ? parseInt(idParam, 10) : null;

    if (objectId && !isNaN(objectId) && objectId > 0) {
      this.editObjectId = objectId;
      this.editObjectIdSignal.set(objectId);
      this.loadBasicForEdit(objectId);
    } else {
      this.stateService.initialize(this.mode(), this.initialData());
    }
  }

  /**
   * Load only basic info on init (step 1). Types and pricing are loaded lazily.
   */
  private loadBasicForEdit(id: number): void {
    this.loadingObject.set(true);

    this.objectService.getObjectBasic(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (entity) => {
          const basicInfo = this.mapEntityToBasicInfo(entity);
          this.stateService.initializeBasic('edit', basicInfo);
          this.loadedSteps.add(0);
          this.loadingObject.set(false);
        },
        error: () => {
          this.loadingObject.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('errors.errorMessageTitle'),
            detail: this.translocoService.translate('objects.wizard.messages.loadError'),
          });
          this.router.navigate(['/objects/list']);
        },
      });
  }

  /**
   * Load types data for step 2 (lazy, called when navigating to step 1→2)
   */
  private loadTypesForEdit(id: number): void {
    this.loadingStep.set(true);

    this.objectService.getObjectTypes(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (typeEntities) => {
          const unitTypes = this.mapTypeEntitiesToUnitTypes(typeEntities);
          this.stateService.setTypesData(unitTypes);
          this.loadedSteps.add(1);
          this.loadingStep.set(false);
        },
        error: () => {
          this.loadingStep.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('errors.errorMessageTitle'),
            detail: this.translocoService.translate('objects.wizard.messages.loadError'),
          });
        },
      });
  }

  /**
   * Load pricing data for step 3 (lazy, called when navigating to step 2→3)
   */
  private loadPricingForEdit(id: number): void {
    this.loadingStep.set(true);

    this.objectService.getObjectPricing(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (pricing) => {
          const { pricingRules, seasons } = this.mapPricingResponse(pricing);
          this.stateService.setPricingData(pricingRules, seasons);
          this.loadedSteps.add(2);
          this.loadingStep.set(false);
        },
        error: () => {
          this.loadingStep.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('errors.errorMessageTitle'),
            detail: this.translocoService.translate('objects.wizard.messages.loadError'),
          });
        },
      });
  }

  // --- Mapping helpers (split from the old monolithic mapEntityToVacationProperty) ---

  private mapEntityToBasicInfo(entity: ManagedObjectEntity): PropertyBasicInfo {
    return {
      name: entity.name,
      registrationNumber: entity.registrationNumber || '',
      description: entity.description || '',
      address: {
        city: entity.address?.city || '',
        street: entity.address?.street || '',
        streetNumber: entity.address?.streetNumber || '',
        postalCode: entity.address?.postalCode || '',
        country: entity.address?.country || 'Polska',
      },
      images: (entity.images || []).map((img, i) => ({
        id: `img-${i}`,
        url: img.url,
        variants: img.variants,
      } as ImageFile)),
      amenityIds: entity.amenityIds,
      location: entity.latitude != null && entity.longitude != null
        ? { latitude: entity.latitude, longitude: entity.longitude }
        : undefined,
      // Pole formularza = tylko wpis właściciela; wartość auto (linia prosta) idzie do podpowiedzi
      distanceToSeaM: entity.distanceToSeaSource === 'owner' ? entity.distanceToSeaM : null,
      distanceToSeaAuto: entity.distanceToSeaSource === 'auto' ? entity.distanceToSeaM : null,
      phone: entity.phone || '',
      email: entity.email || '',
      whatsApp: entity.whatsApp || '',
      contactVisibility: entity.contactVisibility ?? { ...DEFAULT_CONTACT_VISIBILITY },
    };
  }

  private mapTypeEntitiesToUnitTypes(types: ObjectTypeEntity[]): UnitType[] {
    return types.map((t, i) => {
      const activeUnits = t.units?.filter(u => u.status === 'active') ?? [];
      const unitNamesFromUnits = activeUnits.length > 0
        ? activeUnits.map(u => u.name)
        : (t.unitNames || []);
      const hasManualNames = unitNamesFromUnits.length > 0;

      return {
        id: String(t.id),
        name: t.name,
        description: t.description || '',
        propertyTypeId: t.propertyTypeId ?? null,
        quantity: t.quantity || 1,
        maxOccupancy: t.capacity || 1,
        namingPattern: hasManualNames ? '' : (t.namingPattern || `${t.name} {n}`),
        namingMode: hasManualNames ? 'manual' as const : 'auto' as const,
        unitNames: unitNamesFromUnits,
        units: activeUnits,
        images: (t.images || []).map((img, j) => ({
          id: `type-${i}-img-${j}`,
          url: img.url,
          variants: img.variants,
        } as ImageFile)),
        amenityIds: t.amenityIds,
        squareMeters: t.squareMeters ?? undefined,
        bedrooms: t.bedrooms ?? undefined,
        beds: t.beds ?? undefined,
        bathrooms: t.bathrooms ?? undefined,
      };
    });
  }

  private mapPricingResponse(response: ObjectPricingResponse): { pricingRules: PricingRules; seasons: Season[] } {
    const pr = response.pricingRules;
    const pricingRules: PricingRules = pr ? {
      electricityPolicy: (pr.electricityPolicy || 'included') as 'included' | 'metered' | 'flat_fee',
      petPolicy: (pr.petPolicy || 'not_allowed') as PetPolicy,
      petFee: pr.petFee != null ? groszeToPln(Number(pr.petFee)) : undefined,
      depositPercentage: pr.depositPercentage != null ? Number(pr.depositPercentage) : 30,
      cleaningFee: pr.cleaningFee != null ? groszeToPln(Number(pr.cleaningFee)) : undefined,
      checkInTime: pr.checkInTime || '15:00',
      checkOutTime: pr.checkOutTime || '10:00',
      minStay: pr.minStay ?? 1,
      maxStay: pr.maxStay ?? undefined,
      depositPaymentDays: pr.depositPaymentDays != null ? Number(pr.depositPaymentDays) : 3,
    } : {
      electricityPolicy: 'included' as const,
      petPolicy: 'not_allowed' as const,
      depositPercentage: 30,
      checkInTime: '15:00',
      checkOutTime: '10:00',
      minStay: 1,
      depositPaymentDays: 3,
    };

    // We need the unit types from state to validate season unitPrices
    const unitTypes = this.stateService.unitTypes();

    const seasons: Season[] = (response.seasons || []).map((s, i) => ({
      id: String(s.id || `season-${i}`),
      name: s.name,
      startDate: new Date(s.startDate),
      endDate: new Date(s.endDate),
      color: s.color || '#3B82F6',
      minStay: s.minStay ?? undefined,
      unitPrices: (s.unitPrices || [])
        .map(up => {
          const resolvedId = String(up.objectTypeId);
          const resolvedType = unitTypes.find(ut => ut.id === resolvedId);
          if (!resolvedType) return null;
          // undefined = brak wyceny za mniej osób (pełna cena). amount = grosze
          // na backendzie → PLN w modelu; percent bez konwersji.
          const op = up.occupancyPricing;
          const occupancyPricing: OccupancyPricing | undefined = op == null
            ? undefined
            : {
                mode: op.mode,
                tiers: op.tiers.map(t => ({
                  occupants: t.occupants,
                  value: op.mode === 'amount' ? groszeToPln(Number(t.value)) : t.value,
                })),
              };
          return {
            unitTypeId: resolvedId,
            pricePerNight: groszeToPln(Number(up.pricePerNight)),
            occupancyPricing,
          };
        })
        .filter(
          (up): up is { unitTypeId: string; pricePerNight: number; occupancyPricing: OccupancyPricing | undefined } =>
            up !== null,
        ),
    }));

    return { pricingRules, seasons };
  }

  onCancel(): void {
    this.formCancel.emit();
    this.router.navigate(['/objects/list']);
  }

  onPrevious(): void {
    this.stateService.previousStep();
    this.stepChange.emit(this.currentStep());
  }

  onNext(): void {
    this.stateService.nextStep();
    this.stepChange.emit(this.currentStep());
  }

  onSubmit(): void {
    if (this.submitting()) {
      return;
    }

    const data = this.stateService.propertyData();
    if (!data) {
      return;
    }

    this.formSubmit.emit(data);
    const dto = this.mapToCreateDto(data);

    if (this.editObjectId) {
      this.pendingDto = dto;
      this.impactLoading.set(true);
      this.showImpactDialog.set(true);

      this.objectService.checkUpdateImpact(this.editObjectId, dto)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (impact) => {
            this.impactResult.set(impact);
            this.impactLoading.set(false);

            if (impact.safe && impact.warnings.length === 0) {
              this.showImpactDialog.set(false);
              this.performUpdate(dto);
            }
          },
          error: () => {
            this.impactLoading.set(false);
            this.showImpactDialog.set(false);
            this.messageService.add({
              severity: 'error',
              summary: this.translocoService.translate('errors.errorMessageTitle'),
              detail: this.translocoService.translate('objects.wizard.updateImpact.checkError'),
            });
          },
        });
    } else {
      this.performCreate(dto);
    }
  }

  onImpactProceed(): void {
    this.showImpactDialog.set(false);
    if (this.pendingDto) {
      this.performUpdate(this.pendingDto);
    }
  }

  onImpactCancel(): void {
    this.showImpactDialog.set(false);
    this.impactResult.set(null);
    this.pendingDto = null;
  }

  private performUpdate(dto: CreateObjectDto): void {
    this.submitting.set(true);

    this.objectService.updateObject(this.editObjectId!, dto)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.pendingDto = null;
          const editObjectId = this.editObjectId;
          this.capyStats.track('property_updated', { ...(editObjectId ? { objectId: editObjectId } : {}) });
          this.messageService.add({
            severity: 'success',
            summary: this.translocoService.translate('globalMessage.success'),
            detail: this.translocoService.translate('objects.wizard.messages.updateSuccess'),
          });
          this.router.navigate(['/objects/list']);
        },
        error: () => {
          this.submitting.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('errors.errorMessageTitle'),
            detail: this.translocoService.translate('objects.wizard.messages.updateError'),
          });
        },
      });
  }

  private performCreate(dto: CreateObjectDto): void {
    this.submitting.set(true);

    this.objectService.createObject(dto)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          const unitTypeCount = this.stateService.unitTypes().length;
          const seasonCount = this.stateService.seasons().length;
          this.capyStats.track('property_created', { mode: 'create', unitTypeCount, seasonCount });
          this.messageService.add({
            severity: 'success',
            summary: this.translocoService.translate('globalMessage.success'),
            detail: this.translocoService.translate('objects.wizard.messages.createSuccess'),
          });
          this.router.navigate(['/objects/list']);
        },
        error: () => {
          this.submitting.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translocoService.translate('errors.errorMessageTitle'),
            detail: this.translocoService.translate('objects.wizard.messages.createError'),
          });
        },
      });
  }

  goToStep(step: number): void {
    this.stateService.goToStep(step);
    this.stepChange.emit(step);
  }

  /**
   * Handles a positioning hint action: jump to the relevant wizard step
   * (calendar/profile) or navigate to another module (reviews/calendar).
   */
  onPositioningNavigate(hint: PositioningHint): void {
    const { target } = hint;
    if (target.kind === 'wizardStep') {
      this.goToStep(target.step);
    } else if (target.kind === 'route') {
      this.router.navigate([target.route]);
    }
  }

  onStepChange(): void {
    this.stepChange.emit(this.currentStep());
  }

  /**
   * Ensure step data is loaded before navigating (lazy loading for edit mode).
   * In create mode or if step data is already loaded, this is a no-op.
   */
  private ensureStepDataLoaded(step: number): void {
    if (!this.editObjectId || this.loadedSteps.has(step)) {
      return;
    }

    if (step === 1) {
      this.loadTypesForEdit(this.editObjectId);
    } else if (step === 2) {
      this.loadPricingForEdit(this.editObjectId);
    }
  }

  /**
   * Builds the units[] payload for a unit type update.
   * - Existing units keep their id (rename if name changed)
   * - New units (quantity increased) are sent without id
   * - Units removed from the list (quantity decreased) are omitted → backend soft-deletes
   */
  private buildUnitsPayload(ut: UnitType): { id?: number; name: string }[] | undefined {
    const names = ut.unitNames;
    if (names.length === 0) return undefined;

    const existingUnits = ut.units ?? [];
    const result: { id?: number; name: string }[] = [];

    for (let i = 0; i < names.length; i++) {
      if (i < existingUnits.length) {
        // Existing unit — preserve id, use (possibly updated) name
        result.push({ id: existingUnits[i].id, name: names[i] });
      } else {
        // New unit — no id
        result.push({ name: names[i] });
      }
    }

    return result;
  }

  /**
   * Format date as YYYY-MM-DD using local timezone (avoids UTC shift from toISOString).
   */
  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private mapToCreateDto(property: VacationProperty): CreateObjectDto {
    const { basicInfo, unitTypes, pricingRules, seasons } = property;

    const types: CreateObjectTypeInlineDto[] = unitTypes.map((ut) => ({
      id: !isNaN(parseInt(ut.id, 10)) ? parseInt(ut.id, 10) : undefined,
      name: ut.name,
      propertyTypeId: ut.propertyTypeId || undefined,
      description: ut.description || undefined,
      images: ut.images
        .filter(img => img.url && !img.isUploading && !img.url.startsWith('blob:'))
        .map(img => ({ url: img.url, variants: img.variants })),
      capacity: ut.maxOccupancy,
      quantity: ut.quantity,
      unitNames: ut.unitNames.length > 0 ? [...ut.unitNames] : undefined,
      units: this.buildUnitsPayload(ut),
      squareMeters: ut.squareMeters,
      bedrooms: ut.bedrooms,
      beds: ut.beds,
      bathrooms: ut.bathrooms,
      amenityIds: ut.amenityIds.length > 0 ? [...ut.amenityIds] : undefined,
    }));

    const seasonDtos: CreateSeasonInlineDto[] = seasons.map(s => ({
      name: s.name,
      startDate: s.startDate instanceof Date ? this.formatLocalDate(s.startDate) : String(s.startDate),
      endDate: s.endDate instanceof Date ? this.formatLocalDate(s.endDate) : String(s.endDate),
      color: s.color || undefined,
      minStay: s.minStay || undefined,
      unitPrices: s.unitPrices
        .map(up => {
          const matchedType = unitTypes.find(ut => ut.id === up.unitTypeId);
          if (!matchedType) return null;
          // undefined = brak wyceny za mniej osób (pełna cena). amount = PLN w
          // modelu → grosze na backend; percent bez konwersji.
          const op = up.occupancyPricing;
          const occupancyPricing: OccupancyPricing | undefined = op == null
            ? undefined
            : {
                mode: op.mode,
                tiers: op.tiers.map(t => ({
                  occupants: t.occupants,
                  value: op.mode === 'amount' ? plnToGrosze(Number(t.value)) : t.value,
                })),
              };
          return {
            unitTypeName: matchedType.name,
            pricePerNight: plnToGrosze(Number(up.pricePerNight)),
            occupancyPricing,
          };
        })
        .filter(
          (up): up is { unitTypeName: string; pricePerNight: number; occupancyPricing: OccupancyPricing | undefined } =>
            up !== null,
        ),
    }));

    return {
      name: basicInfo.name,
      registrationNumber: basicInfo.registrationNumber || undefined,
      description: basicInfo.description,
      address: {
        city: basicInfo.address.city,
        street: basicInfo.address.street,
        streetNumber: basicInfo.address.streetNumber,
        postalCode: basicInfo.address.postalCode,
        country: basicInfo.address.country || 'Polska',
      },
      latitude: basicInfo.location?.latitude,
      longitude: basicInfo.location?.longitude,
      // null = brak wpisu właściciela → backend liczy/odświeża wartość automatyczną
      distanceToSeaM: basicInfo.distanceToSeaM ?? null,
      images: basicInfo.images
        .filter(img => img.url && !img.isUploading && !img.url.startsWith('blob:'))
        .map(img => ({ url: img.url, variants: img.variants })),
      amenityIds: basicInfo.amenityIds.length > 0 ? [...basicInfo.amenityIds] : undefined,
      phone: basicInfo.phone || undefined,
      email: basicInfo.email || undefined,
      whatsApp: basicInfo.whatsApp || undefined,
      contactVisibility: basicInfo.contactVisibility,
      types,
      pricingRules: {
        electricityPolicy: pricingRules.electricityPolicy,
        petPolicy: pricingRules.petPolicy,
        petFee: pricingRules.petFee != null ? plnToGrosze(pricingRules.petFee) : undefined,
        depositPercentage: pricingRules.depositPercentage,
        cleaningFee: pricingRules.cleaningFee != null ? plnToGrosze(pricingRules.cleaningFee) : undefined,
        checkInTime: pricingRules.checkInTime,
        checkOutTime: pricingRules.checkOutTime,
        minStay: pricingRules.minStay,
        maxStay: pricingRules.maxStay,
        depositPaymentDays: pricingRules.depositPaymentDays,
      },
      seasons: seasonDtos,
    };
  }
}
